import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard, PageTitle } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, Building2, Boxes, Inbox, Briefcase, DollarSign, AlertTriangle,
  TrendingUp, HeartHandshake, Flame,
} from "lucide-react";

const money = (n) => "$" + (Math.round(n || 0)).toLocaleString();

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-slate-400">Loading…</p>;

  return (
    <div>
      <PageTitle title="Admin Dashboard" subtitle="Network overview — you primarily review exceptions" />
      {s.open_exceptions > 0 && (
        <Card className="mb-6 flex items-center justify-between border-amberbrand/40 bg-amberbrand/5 p-5 cursor-pointer" onClick={() => nav("/admin/exceptions")} data-testid="exceptions-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-amberbrand" />
            <div>
              <p className="font-heading font-semibold text-navy">{s.open_exceptions} open exception{s.open_exceptions > 1 ? "s" : ""} need review</p>
              <p className="text-sm text-slate-500">Human involvement is concentrated in the Exception Queue.</p>
            </div>
          </div>
          <span className="text-sm font-semibold text-amberbrand">Review →</span>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard tid="admin-stat-members" label="Members" value={s.members} icon={Users} />
        <StatCard tid="admin-stat-applicants" label="Agent Applicants" value={s.agent_applicants} icon={UserCheck} accent="amber" />
        <StatCard tid="admin-stat-active-agents" label="Active Agents" value={s.active_agents} icon={UserCheck} accent="emerald" />
        <StatCard tid="admin-stat-providers" label="Providers" value={s.providers} icon={Building2} />
        <StatCard tid="admin-stat-capacity" label="Care Capacity" value={s.care_capacity} icon={Boxes} accent="navy" />
        <StatCard tid="admin-stat-refs" label="Referral Sources" value={s.referral_sources} icon={HeartHandshake} />
        <StatCard tid="admin-stat-leads" label="Leads" value={s.leads} icon={Inbox} />
        <StatCard tid="admin-stat-qualified" label="Qualified Opps" value={s.qualified_opportunities} icon={Briefcase} accent="emerald" />
        <StatCard tid="admin-stat-placements" label="Placements" value={s.placements} icon={TrendingUp} accent="emerald" />
        <StatCard tid="admin-stat-conversion" label="Conversion Rate" value={`${s.conversion_rate}%`} icon={TrendingUp} accent="navy" />
        <StatCard tid="admin-stat-accel" label="Accelerator Customers" value={s.accelerator_customers} icon={Flame} accent="amber" />
        <StatCard tid="admin-stat-exceptions" label="Open Exceptions" value={s.open_exceptions} icon={AlertTriangle} accent="amber" />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="p-6">
          <h3 className="font-heading font-semibold text-navy">Commission pipeline</h3>
          <div className="mt-4 space-y-3">
            <Line label="Potential" value={money(s.potential_commissions)} />
            <Line label="Approved" value={money(s.approved_commissions)} accent="text-emeraldbrand" />
            <Line label="Paid" value={money(s.paid_commissions)} accent="text-emeraldbrand" />
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-heading font-semibold text-navy">Top service categories</h3>
          <div className="mt-4 space-y-2">
            {(s.top_services || []).map(([name, count]) => (
              <div key={name} className="flex justify-between text-sm"><span className="text-slate-600">{name}</span><span className="font-semibold text-navy">{count}</span></div>
            ))}
            {(!s.top_services || s.top_services.length === 0) && <p className="text-sm text-slate-400">No lead data yet.</p>}
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="font-heading font-semibold text-navy">Top markets</h3>
          <div className="mt-4 space-y-2">
            {(s.top_markets || []).map(([name, count]) => (
              <div key={name} className="flex justify-between text-sm"><span className="text-slate-600">{name}</span><span className="font-semibold text-navy">{count}</span></div>
            ))}
            {(!s.top_markets || s.top_markets.length === 0) && <p className="text-sm text-slate-400">No market data yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
function Line({ label, value, accent = "text-navy" }) {
  return <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-2.5"><span className="text-sm text-slate-600">{label}</span><span className={`font-heading font-bold ${accent}`}>{value}</span></div>;
}
